import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAuthService } from '../services/userAuthService';

interface UserRegistrationPageProps {
  onRegistrationSuccess: () => void;
}

const UserRegistrationPage: React.FC<UserRegistrationPageProps> = ({ onRegistrationSuccess }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'mobile' | 'otp' | 'form'>('mobile');
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    email_id: '',
    user_name: '',
    user_city: '',
    user_state: '',
    user_pincode: '',
    date_of_birth: '',
    nominee_name: '',
    nominee_date_of_birth: '',
    nominee_relation: ''
  });

  const handleGenerateRegistrationOTP = async () => {
    if (!mobileNumber) {
      setError('Please enter mobile number');
      return;
    }

    if (!userAuthService.validateMobileNumber(mobileNumber)) {
      setError('Please enter a valid Indian mobile number');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Send mobile number without country code (API expects 10-digit number)
      const response = await fetch(`${userAuthService['baseUrl']}/generate-registration-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobile_number: mobileNumber }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage(`Registration OTP sent successfully! (For testing: ${result.otp})`);
        setStep('otp');
      } else {
        setError(result.error || 'Failed to generate registration OTP');
      }
    } catch (err) {
      setError('Failed to generate registration OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateRegistrationOTP = async () => {
    if (!otp) {
      setError('Please enter OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send mobile number without country code (API expects 10-digit number)
      const response = await fetch(`${userAuthService['baseUrl']}/validate-registration-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          mobile_number: mobileNumber, 
          otp: otp 
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage('OTP validated successfully! Please fill in your details.');
        setStep('form');
      } else {
        setError(result.error || 'Failed to validate OTP');
      }
    } catch (err) {
      setError('Failed to validate OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = ['email_id', 'user_name', 'user_pincode', 'date_of_birth', 'nominee_name', 'nominee_date_of_birth', 'nominee_relation'];
    for (const field of requiredFields) {
      if (!formData[field as keyof typeof formData]) {
        setError(`Please fill in ${field.replace('_', ' ')}`);
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      // Send mobile number without country code (API expects 10-digit number)
      const userData = {
        mobile_number: mobileNumber,
        ...formData
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage('Registration successful! You can now login with your mobile number.');
        setTimeout(() => {
          onRegistrationSuccess();
          navigate('/users');
        }, 2000);
      } else {
        setError(result.error || 'Failed to create user account');
      }
    } catch (err) {
      setError('Failed to create user account');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToMobile = () => {
    setStep('mobile');
    setOtp('');
    setError('');
    setMessage('');
  };

  const handleBackToOTP = () => {
    setStep('otp');
    setError('');
    setMessage('');
  };

  const handleBackToMain = () => {
    navigate('/');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="w-full max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={handleBackToMain}
          className="text-white hover:text-orange-200 transition-colors mb-8 font-semibold"
        >
          ← Back to Main
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center space-x-6 mb-6">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3">
                <img 
                  src="/images/Asset3.png" 
                  alt="Mandir Setu" 
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
              <h2 className="text-lg font-bold text-white text-center">Mandir Setu</h2>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3">
                <img 
                  src="/images/Asset2.png" 
                  alt="श्री मार्तंड देवसंस्थान" 
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
              <h2 className="text-lg font-bold text-white text-center">श्री मार्तंड देवसंस्थान</h2>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2 text-center">User Registration</h1>
          <p className="text-white/80 text-center">Create your temple account</p>
        </div>

        {/* Main Card */}
        <div className="max-w-md mx-auto">
          <div className="bg-white/10 rounded-lg p-6">
            {step === 'mobile' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    placeholder="Enter 10-digit number"
                    maxLength={10}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                  />
                </div>

                <button
                  onClick={handleGenerateRegistrationOTP}
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-500 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
                >
                  {loading ? 'Sending OTP...' : 'Send Registration OTP'}
                </button>

                {/* Registration Info */}
                <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
                  <h3 className="text-blue-200 font-semibold mb-2">ℹ️ Registration Process</h3>
                  <div className="text-xs text-blue-100 space-y-1">
                    <p>1. Enter your mobile number</p>
                    <p>2. Verify with OTP</p>
                    <p>3. Fill in your details</p>
                    <p>4. Complete registration</p>
                  </div>
                </div>
              </div>
            )}

            {step === 'otp' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    OTP Verification
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-center text-2xl tracking-widest text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                  />
                  <p className="text-xs text-white/80 mt-1 text-center">
                    Enter the 6-digit code sent to {mobileNumber}
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handleBackToMobile}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleValidateRegistrationOTP}
                    disabled={loading}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-500 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
                  >
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </div>
              </div>
            )}

            {step === 'form' && (
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Email ID *
                  </label>
                  <input
                    type="email"
                    name="email_id"
                    value={formData.email_id}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      name="user_city"
                      value={formData.user_city}
                      onChange={handleInputChange}
                      placeholder="City"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      name="user_state"
                      value={formData.user_state}
                      onChange={handleInputChange}
                      placeholder="State"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    PIN Code *
                  </label>
                  <input
                    type="text"
                    name="user_pincode"
                    value={formData.user_pincode}
                    onChange={handleInputChange}
                    placeholder="Enter PIN code"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    required
                  />
                </div>

                <div className="border-t border-white/20 pt-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Nominee Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Nominee Name *
                    </label>
                    <input
                      type="text"
                      name="nominee_name"
                      value={formData.nominee_name}
                      onChange={handleInputChange}
                      placeholder="Enter nominee name"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Nominee DOB *
                      </label>
                      <input
                        type="date"
                        name="nominee_date_of_birth"
                        value={formData.nominee_date_of_birth}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Relation *
                      </label>
                      <select
                        name="nominee_relation"
                        value={formData.nominee_relation}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                        required
                      >
                        <option value="">Select relation</option>
                        <option value="spouse">Spouse</option>
                        <option value="son">Son</option>
                        <option value="daughter">Daughter</option>
                        <option value="father">Father</option>
                        <option value="mother">Mother</option>
                        <option value="brother">Brother</option>
                        <option value="sister">Sister</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleBackToOTP}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-500 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </div>
              </form>
            )}

            {error && (
              <div className="mt-4 bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            )}

            {message && (
              <div className="mt-4 bg-green-500/20 border border-green-500/30 rounded-lg p-3">
                <p className="text-green-200 text-sm">{message}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* AUGMONT Logo */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">Powered By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/Asset1.png" 
                    alt="AUGMONT GOLD" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Mandirsetu Initiative */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">An Initiative By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/mandirSetu.png" 
                    alt="An Initiative By Mandirsetu" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRegistrationPage; 